import type { PropsWithChildren } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  ApiAppLegacyProvider,
  ApiV2Provider,
  ApiWebLegacyProvider,
  queryClient,
} from '@/shared/api';
import { ErrorBoundary } from '@/shared/error-boundary';
import { NavigatorProvider } from '@/shared/navigation';
import { OverlayHost } from '@/shared/overlay';
import { Toaster } from '@/shared/ui';
import { useThemeStore } from '@/shared/stores';
import { VersionUpdateGate } from '@/widgets/version-update-gate';
import { apiAppLegacy, apiV2, apiWebLegacy, appNavigator } from '@/app/runtime';

/**
 * 토스트 호스트. heritage `<Toaster/>` 는 색을 heviton CSS 토큰(`--popover` 등)에 묶지만
 * light/dark *선택* 은 next-themes(provider 없음 → OS 추종)를 본다. heviton 은 자체 theme
 * store 로 강제 override(`.dark` 클래스)를 지원하므로 `theme={mode}` 를 주입해 정합시킨다.
 * position 은 모바일 우선 기본(top-center, 하단 safe-area·home indicator 회피) — 반응형
 * 정책(D9) 확정 시 재검토. 별도 컴포넌트로 분리해 테마 변경이 provider 트리 전체를
 * 리렌더하지 않도록 한다.
 *
 * top 오프셋 = sonner 기본값(desktop 24px / mobile 16px) + `env(safe-area-inset-top)` — 모바일
 * WebView(<600px)는 `mobileOffset` 이 실제 적용되므로 둘 다 지정해야 노치를 피한다(데스크탑은 env=0 → 무변화).
 */
function AppToaster() {
  const mode = useThemeStore((s) => s.mode);
  return (
    <Toaster
      theme={mode}
      position="top-center"
      offset={{ top: 'calc(env(safe-area-inset-top) + 24px)' }}
      mobileOffset={{ top: 'calc(env(safe-area-inset-top) + 16px)' }}
    />
  );
}

/**
 * 전역 Provider 합성. composition 의 singleton 을 React 트리에 노출한다 —
 * 트리 안 hook (`useApiAppLegacy`, `useNavigator`) 과 트리 밖 import 가 같은 인스턴스를 본다.
 *
 * 순서 강제는 하나: `ErrorBoundary` 가 최외곽 (안쪽 throw 를 잡으려면 외곽이어야 한다).
 * 나머지 Provider 는 서로 독립이라 어느 순서든 동작 동일.
 */
export function AppProvider({ children }: PropsWithChildren) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ApiAppLegacyProvider api={apiAppLegacy}>
          <ApiV2Provider api={apiV2}>
            <ApiWebLegacyProvider api={apiWebLegacy}>
              <NavigatorProvider navigator={appNavigator}>
                {children}
                <OverlayHost />
                <AppToaster />
                <VersionUpdateGate />
              </NavigatorProvider>
            </ApiWebLegacyProvider>
          </ApiV2Provider>
        </ApiAppLegacyProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
